<script lang="ts">
  import type { Place } from '../api/types'
  import { fmtCoords, placeLabel } from '../format'
  import type { AlertFeature } from '../map/layers/featureInfo'
  import { fetchLegendFor, swatchFor, type LegendSpec } from '../map/layers/legendSource'
  import { formatReadout, type LayerReadout, type MdHit, type PointProbe } from '../map/layers/probe'
  import { ALERTS_ID, getLayerDef, MD_ID } from '../map/layers/registry'
  import { layers } from '../state/layers.svelte'
  import { alertTone } from '../forecast/tone'
  import { layout } from '../state/layout.svelte'
  import Icon from './Icon.svelte'
  import { askPersist, MAX_PLACES, places, savedFor, savePlace } from '../state/places.svelte'

  /**
   * What a tap on the map opens.
   *
   * A click used to fetch a forecast outright, which made tapping an alert
   * polygon do two unrelated things at once. Now the click only asks the map
   * what is there: the value of every active overlay under the finger, any
   * alerts covering it, and a button to move the forecast here. Nothing
   * happens to the sidebar until that button is pressed.
   *
   * Drawn as round 2 has it (Stage 10.5, 2c): a card beside the pin on the
   * desktop and a sheet from the bottom edge on a phone, with the values
   * under "At this point", then the alerts and the SPC products — each
   * section only when its layer is on, because "None at this point" from a
   * layer nobody turned on is a claim nobody made — and the two actions
   * pinned at the foot.
   */

  interface Props {
    /** CSS pixels within the map container. */
    x: number
    y: number
    lat: number
    lon: number
    probe: PointProbe | null
    /** null until the lookup lands, or for a point outside NWS coverage. */
    place: Place | null
    /** The lookup has answered, so `place` is final: the name to save under. */
    placeSettled?: boolean
    /** NWS answered that it has no point forecast here: the ocean, Mexico. */
    outside?: boolean
    onForecast: () => void
    onAlert: (alert: AlertFeature & { capId: string }) => void
    onMd: (md: MdHit) => void
    onClose: () => void
  }

  let {
    x,
    y,
    lat,
    lon,
    probe,
    place,
    placeSettled = true,
    outside = false,
    onForecast,
    onAlert,
    onMd,
    onClose,
  }: Props = $props()

  // "8 mi from Lawrence, KS" rather than a claim about somewhere else.
  let placeName = $derived(place ? placeLabel(place) : '')

  // "Save this place" (plan §6.3): the name is the lookup's, so it waits for
  // it. A point within 1 km of a saved place already is that place.
  let saved = $derived(savedFor({ lat, lon }))
  let full = $derived(places.list.length >= MAX_PLACES)

  function save() {
    void askPersist() // Inside the gesture.
    savePlace({
      lat,
      lon,
      name: placeName || fmtCoords(lat, lon),
      label: placeName || undefined,
    })
  }

  let wrap: HTMLElement | undefined = $state()
  let menu: HTMLElement | undefined = $state()
  let placement = $state({ left: 0, top: 0, above: true })

  const time = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })
  let frame = $derived(layers.times[Math.min(layers.index, layers.times.length - 1)] ?? null)

  /**
   * A layer draws its own frame nearest the slider, and an hourly forecast
   * next to a four-minute radar loop can be most of an hour away. Say so on
   * the row rather than letting one header speak for both.
   */
  function offFrame(readout: { time: Date | null }): string {
    if (!readout.time || !frame) return ''
    return Math.abs(readout.time.getTime() - frame.getTime()) < 60_000
      ? ''
      : time.format(readout.time)
  }

  const dayTime = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })

  /**
   * An SPC outlook is a discrete product with a validity window, not a frame
   * on the shared axis, so it gets a second line instead of an `At` header:
   * "Thu 3:00 PM → Fri 7:00 AM", in the viewer's own zone like everything else
   * in the app.
   */
  function validWindow(readout: LayerReadout): string {
    if (!readout.valid) return ''
    return `${dayTime.format(readout.valid.from)} → ${dayTime.format(readout.valid.to)}`
  }

  /**
   * Whether the answer takes its own line.
   *
   * `.name` is the row's flexible column, so anything wide on the right
   * squeezes the layer name rather than being squeezed itself. A category
   * ("General Thunderstorms Risk") always was wide; a *number* became wide
   * when lightning arrived, whose value carries its units with it — on a phone
   * "0.0 strikes/1000 km²/min" left the label at zero pixels.
   */
  function stacks(readout: LayerReadout): boolean {
    return readout.text !== null || formatReadout(readout).length > 14
  }

  /**
   * The desktop card sits beside the pin, on whichever side has room, and
   * centred on it vertically as far as the map allows; it clears the search
   * row along the top. The phone's is a sheet and is not placed.
   */
  const CARD_TOP = 72
  const MARGIN = 8
  const GAP = 24

  function reposition() {
    if (!layout.isDesktop) return
    const box = wrap?.parentElement?.getBoundingClientRect()
    const size = menu?.getBoundingClientRect()
    if (!box || !size) return
    // The desktop's map runs under the rail and the panel; the card stays
    // on the open map, right of where the layout says it starts.
    const inset = wrap ? parseFloat(getComputedStyle(wrap).getPropertyValue('--chrome-left')) : NaN
    const floor = Number.isFinite(inset) ? inset : MARGIN
    const right = x + GAP + size.width <= box.width - MARGIN || x - GAP - size.width < floor
    const left = right ? x + GAP : Math.max(floor, x - GAP - size.width)
    const top = Math.min(
      Math.max(CARD_TOP, y - size.height / 2),
      Math.max(CARD_TOP, box.height - MARGIN - size.height),
    )
    placement = { left, top, above: false }
  }

  // Re-place whenever the anchor or the contents move: the probe arriving adds
  // rows, which changes the height the placement depends on.
  $effect(() => {
    void x
    void y
    void probe
    void place
    void swatches
    reposition()
  })

  /**
   * What each group of the menu is about. A section is drawn only when its
   * layer is on: "None at this point" is a finding about the alerts layer, and
   * from a map with alerts off it would be a claim nobody made.
   */
  const isOutlook = (id: string) => getLayerDef(id)?.group === 'outlooks'
  let values = $derived(probe?.readouts.filter((r) => !isOutlook(r.id)) ?? [])
  let outlooks = $derived(probe?.readouts.filter((r) => isOutlook(r.id)) ?? [])
  // What was found counts as well as what is on: the probe is the stack's
  // answer, and a found alert is shown whatever the stack does next.
  let alertsOn = $derived(layers.active.includes(ALERTS_ID) || (probe?.alerts.length ?? 0) > 0)
  let mdsOn = $derived(layers.active.includes(MD_ID) || (probe?.mds.length ?? 0) > 0)
  let outlookOn = $derived(layers.active.some(isOutlook) || outlooks.length > 0)

  /** While the probe is out: one waiting row per layer that will answer. */
  let pending = $derived(
    [...layers.active]
      .reverse()
      .map(getLayerDef)
      .filter((d) => d?.readout && !isOutlook(d.id))
      .map((d) => d!.label),
  )

  /**
   * The colour each reading sits in, from the layer's own legend (the one the
   * colour bar already fetched, out of the same cache). A categorical answer
   * and a reading with no colour get an empty box rather than a guess.
   */
  let swatches = $state<Record<string, string | null>>({})
  $effect(() => {
    const readouts = probe?.readouts ?? []
    let cancelled = false
    void Promise.all(
      readouts.map(async (r): Promise<[string, string | null]> => {
        const ref = getLayerDef(r.id)?.legend
        if (!ref || r.value === null) return [r.id, null]
        const spec: LegendSpec | null = await fetchLegendFor(ref).catch(() => null)
        return [r.id, swatchFor(spec, r.value)]
      }),
    ).then((pairs) => {
      if (!cancelled) swatches = Object.fromEntries(pairs)
    })
    return () => {
      cancelled = true
    }
  })

  const until = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })

  /** "Warning · until 8:15 PM MDT": the level word, then when it ends. */
  function alertMeta(alert: AlertFeature): string {
    const end = alert.expiration ? Date.parse(alert.expiration) : NaN
    return Number.isFinite(end) ? `Until ${until.format(end)}` : ''
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') onClose()
  }
</script>

<svelte:window onkeydown={onKeydown} />

{#snippet alertBody(alert: AlertFeature)}
  <span class="tag" data-tone={alertTone(alert.prodType)} aria-hidden="true"><Icon name="triangle-alert" size={14} strokeWidth={2.5} /></span>
  <span class="link-text">
    <span class="link-name">{alert.prodType}</span>
    {#if alertMeta(alert)}<span class="link-meta">{alertMeta(alert)}</span>{/if}
  </span>
{/snippet}

{#snippet valueRow(readout: LayerReadout)}
  <!-- A number shares the row with its label; "General Thunderstorms Risk"
       does not fit beside one and squeezing it truncates the layer name. -->
  <div class="row" class:stacked={stacks(readout)}>
    <span
      class="swatch"
      class:empty={!swatches[readout.id]}
      style:background={swatches[readout.id] ?? undefined}
      aria-hidden="true"
    ></span>
    <span class="name">
      {readout.label}
      {#if offFrame(readout)}<span class="off">{offFrame(readout)}</span>{/if}
      {#if validWindow(readout)}<span class="valid">{validWindow(readout)}</span>{/if}
    </span>
    <span class="value" class:absent={readout.value === null && readout.text === null}>
      {formatReadout(readout)}
    </span>
  </div>
{/snippet}

<div class="menu-wrap" class:phone={!layout.isDesktop} bind:this={wrap}>
  <!-- The anchor, so it is obvious which pixel the numbers describe: 1B's
       pin, hollow, with the point in the middle. -->
  <span class="anchor" style:left="{x}px" style:top="{y}px" aria-hidden="true"
    ><span class="dot"></span></span
  >

  <div
    class="map-menu"
    class:sheet={!layout.isDesktop}
    style:left={layout.isDesktop ? `${placement.left}px` : undefined}
    style:top={layout.isDesktop ? `${placement.top}px` : undefined}
    bind:this={menu}
    role="dialog"
    aria-label="Point details"
    tabindex="-1"
    onpointerdown={(e) => e.stopPropagation()}
  >
    {#if !layout.isDesktop}<span class="grab" aria-hidden="true"></span>{/if}
    <header>
      <span class="where">
        {#if placeName}
          <span class="place">{placeName}</span>
        {:else if !placeSettled}
          <span class="skeleton title-skeleton" aria-hidden="true"></span>
        {:else if outside}
          <span class="nowhere">No NWS forecast here</span>
        {/if}
        <span class="coords">{lat.toFixed(4)}, {lon.toFixed(4)}</span>
      </span>
      <button type="button" class="close touch-target" aria-label="Close" onclick={onClose}>
        <Icon name="x" size={layout.isDesktop ? 18 : 22} strokeWidth={2.2} />
      </button>
    </header>

    <div class="body">
      {#if probe === null}
        <!-- `.loading` is what `make e2e` waits on to know the probe landed. -->
        <p class="visually-hidden loading" role="status">Reading the map…</p>
        {#if pending.length > 0}
          <section class="readouts">
            <h3 class="eyebrow">At this point</h3>
            {#each pending as label (label)}
              <div class="row">
                <span class="swatch empty" aria-hidden="true"></span>
                <span class="name">{label}</span>
                <span class="skeleton value-skeleton" aria-hidden="true"></span>
              </div>
            {/each}
          </section>
        {/if}
        {#if alertsOn || mdsOn || outlookOn}
          <section class="checking">
            <span>Checking {alertsOn ? 'alerts' : ''}{alertsOn && (mdsOn || outlookOn) ? ' and ' : ''}{mdsOn || outlookOn ? 'SPC products' : ''}…</span>
            <span class="skeleton" style:width="70%" aria-hidden="true"></span>
            <span class="skeleton" style:width="50%" aria-hidden="true"></span>
          </section>
        {/if}
      {:else}
        {#if values.length > 0}
          <section class="readouts">
            <h3 class="eyebrow">
              At this point
              {#if frame}<span class="at">At {time.format(frame)}</span>{/if}
            </h3>
            {#each values as readout (readout.id)}{@render valueRow(readout)}{/each}
          </section>
        {/if}

        {#if alertsOn}
          <section class="alerts">
            {#if layout.isDesktop}<h3 class="eyebrow">Alerts</h3>{/if}
            {#each probe.alerts as alert (alert.key)}
              {#if alert.capId}
                {@const capId = alert.capId}
                <button type="button" class="link-row alert-row touch-target" onclick={() => onAlert({ ...alert, capId })}>
                  {@render alertBody(alert)}
                  <Icon name="chevron-right" size={16} strokeWidth={2.5} />
                </button>
              {:else}
                <div class="link-row alert-row static">{@render alertBody(alert)}</div>
              {/if}
            {:else}
              <p class="none">{layout.isDesktop ? 'None at this point' : 'No alerts at this point'}</p>
            {/each}
          </section>
        {/if}

        {#if outlookOn || mdsOn}
          <section class="readouts spc">
            {#if layout.isDesktop}<h3 class="eyebrow">SPC risk and discussions</h3>{/if}
            {#each outlooks as readout (readout.id)}{@render valueRow(readout)}{/each}
            {#each probe.mds as md (md.number)}
              <button type="button" class="link-row touch-target" onclick={() => onMd(md)}>
                <Icon name="file-text" size={16} />
                <span class="link-text">
                  <span class="link-name">{md.label.replace(/^MD\b/, 'Mesoscale Discussion')}</span>
                  {#if md.info}<span class="link-meta">{md.info}</span>{/if}
                </span>
                <Icon name="chevron-right" size={16} strokeWidth={2.5} />
              </button>
            {/each}
            {#if mdsOn && probe.mds.length === 0 && outlooks.length === 0}
              <p class="none">No discussions at this point</p>
            {/if}
          </section>
        {/if}

        {#if probe.readouts.length === 0 && probe.alerts.length === 0 && probe.mds.length === 0 && !alertsOn && !mdsOn}
          <p class="hint">No overlays here. Turn some on from Map layers.</p>
        {/if}
      {/if}

      {#if outside}
        <p class="note">
          The National Weather Service has no point forecast here, so there is nothing to
          forecast or save.
        </p>
      {/if}
    </div>

    <footer class="actions" class:disabled={outside}>
      <button type="button" class="action primary touch-target" disabled={outside} onclick={onForecast}>
        Forecast here<Icon name="arrow-right" size={layout.isDesktop ? 16 : 18} strokeWidth={2.5} />
      </button>
      {#if saved}
        <p class="action saved" aria-live="polite">
          <span class="saved-name">Saved<span class="visually-hidden">{` as ${saved.name}`}</span></span>
          <Icon name="star" size={layout.isDesktop ? 16 : 18} filled />
        </p>
      {:else}
        <button
          type="button"
          class="action secondary touch-target"
          aria-label="Save this place"
          title={full ? `${MAX_PLACES} places is the limit` : undefined}
          disabled={!placeSettled || full || outside}
          onclick={save}
        >
          Save<Icon name="star" size={layout.isDesktop ? 16 : 18} />
        </button>
      {/if}
    </footer>
  </div>
</div>

<style>
  .menu-wrap {
    position: absolute;
    inset: 0;
    /* A wrapper only: clicks pass through to the map except on the menu. */
    pointer-events: none;
    z-index: 25;
  }

  /* 1B's tap pin: a hollow ink square ringed in the ground, the point in the
     middle. The home pin is the filled accent one, so the two never read as
     the same mark. */
  .anchor {
    position: absolute;
    width: 16px;
    height: 16px;
    margin: -8px 0 0 -8px;
    border: 3px solid var(--color-text);
    box-shadow: 0 0 0 2px var(--color-bg);
    display: grid;
    place-items: center;
  }

  .dot {
    width: 4px;
    height: 4px;
    background: var(--color-text);
  }

  .map-menu {
    position: absolute;
    pointer-events: auto;
    width: 328px;
    max-width: calc(100% - 16px);
    max-height: min(610px, calc(100% - 72px));
    display: flex;
    flex-direction: column;
    background: var(--color-bg);
    border-top: 2px solid var(--color-text);
    box-shadow: var(--shadow-lg);
    /* Round 2's two densities, as the layer menu has them. */
    --px: 16px;
    --row: 32px;
    --fs: 13px;
    --vfs: 14px;
  }

  .map-menu.sheet {
    left: 0;
    right: 0;
    bottom: 0;
    width: auto;
    max-width: none;
    max-height: min(560px, calc(100% - var(--controls-bottom, 0px) - 12px));
    --px: 20px;
    --row: 40px;
    --fs: 14px;
    --vfs: 15px;
  }

  .grab {
    align-self: center;
    flex: none;
    width: 40px;
    height: 4px;
    margin-top: 8px;
    background: var(--color-divider);
  }

  header {
    flex: none;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 14px 8px 12px var(--px);
  }

  .sheet header {
    padding: 8px 6px 12px var(--px);
  }

  .where {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .place,
  .nowhere {
    font-size: 18px;
    font-weight: 800;
    line-height: 1.15;
  }

  .sheet .place,
  .sheet .nowhere {
    font-size: 20px;
  }

  .coords {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--color-muted);
  }

  .sheet .coords {
    font-size: 13px;
  }

  .close {
    flex: none;
    width: 32px;
    height: 32px;
    display: grid;
    place-items: center;
    border: 0;
    background: none;
    cursor: pointer;
  }

  .sheet .close {
    width: 44px;
    height: 44px;
  }

  .close:hover {
    background: color-mix(in srgb, var(--color-text) 7%, transparent);
  }

  .body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  section {
    display: flex;
    flex-direction: column;
    padding: 0 var(--px) 10px;
    border-top: 2px solid var(--color-divider);
  }

  h3 {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin: 0;
    padding: 10px 0 4px;
  }

  .at {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
    color: var(--color-muted);
    font-variant-numeric: tabular-nums;
  }

  .row {
    display: grid;
    grid-template-columns: 12px minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
    min-height: var(--row);
    padding: 4px 0;
    border-bottom: 1px solid var(--color-divider);
  }

  .swatch {
    width: 12px;
    height: 12px;
    box-shadow: inset 0 0 0 1px rgb(0 0 0 / 25%);
  }

  .swatch.empty {
    box-shadow: none;
    border: 2px solid var(--color-divider);
  }

  .name {
    min-width: 0;
    display: flex;
    flex-direction: column;
    font-size: var(--fs);
    line-height: 1.25;
  }

  .off,
  .valid {
    font-size: 11px;
    color: var(--color-muted);
    font-variant-numeric: tabular-nums;
  }

  .value {
    font-size: var(--vfs);
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .value.absent {
    font-size: var(--fs);
    font-weight: 400;
    color: var(--color-muted);
  }

  /* A categorical answer takes its own line, right-aligned under the layer
     name, so neither has to be truncated. */
  .row.stacked {
    grid-template-columns: 12px minmax(0, 1fr);
  }

  .row.stacked .value {
    grid-column: 2;
    white-space: normal;
    text-align: right;
  }

  .skeleton {
    display: block;
    height: 12px;
    background: color-mix(in srgb, var(--color-text) 12%, transparent);
  }

  .title-skeleton {
    width: 190px;
    height: 18px;
    margin: 2px 0 1px;
  }

  .value-skeleton {
    width: 56px;
  }

  .checking {
    gap: 8px;
    padding-top: 12px;
    padding-bottom: 14px;
    font-size: 12px;
    color: var(--color-muted);
  }

  .link-row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 44px;
    width: 100%;
    padding: 4px 0;
    border: 0;
    border-bottom: 1px solid var(--color-divider);
    background: none;
    text-align: left;
    cursor: pointer;
  }

  .sheet .link-row {
    min-height: 52px;
  }

  .link-row.static {
    cursor: default;
  }

  .link-row:not(.static):hover {
    background: color-mix(in srgb, var(--color-text) 7%, transparent);
  }

  .tag {
    flex: none;
    width: 24px;
    height: 24px;
    display: grid;
    place-items: center;
    background: var(--color-field);
    color: var(--color-on-field);
  }

  .sheet .tag {
    width: 28px;
    height: 28px;
  }

  .link-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .link-name {
    font-size: 14px;
    font-weight: 800;
  }

  .sheet .link-name {
    font-size: 15px;
  }

  .link-meta {
    font-size: 12px;
    color: var(--color-muted);
  }

  .none {
    margin: 0;
    min-height: var(--row);
    display: flex;
    align-items: center;
    font-size: var(--fs);
    color: var(--color-muted);
  }

  .sheet .none {
    border-bottom: 1px solid var(--color-divider);
  }

  /* On the phone the alerts and the SPC rows are one list under one rule, as
     the handoff draws it; the desktop heads each. */
  .sheet .alerts + .spc {
    border-top: 0;
  }

  .hint {
    margin: 0;
    padding: 12px var(--px);
    border-top: 2px solid var(--color-divider);
    font-size: 12px;
    color: var(--color-muted);
  }

  .note {
    margin: 0 var(--px) 12px;
    padding: 8px 10px;
    background: var(--color-surface);
    font-size: 12px;
    line-height: 1.4;
  }

  .actions {
    flex: none;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    padding: 12px var(--px) var(--px);
    border-top: 2px solid var(--color-divider);
  }

  .sheet .actions {
    padding-bottom: calc(var(--px) + var(--safe-bottom));
  }

  .action {
    height: 40px;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 0 12px;
    font-size: 14px;
    font-weight: 800;
    border: 0;
    cursor: pointer;
    white-space: nowrap;
  }

  .sheet .action {
    height: 48px;
    padding: 0 14px;
    font-size: 15px;
  }

  .primary {
    background: var(--color-field);
    color: var(--color-on-field);
  }

  .secondary,
  .saved {
    background: none;
    border: 2px solid var(--color-text);
  }

  .saved {
    cursor: default;
  }

  .saved-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .action:disabled {
    opacity: 0.45;
    cursor: default;
  }

  .primary:not(:disabled):hover {
    background: color-mix(in srgb, var(--color-field) 85%, var(--color-bg));
  }

  .secondary:not(:disabled):hover {
    background: color-mix(in srgb, var(--color-text) 7%, transparent);
  }
</style>
